import { memo, ReactElement, ReactNode, useMemo } from 'react';
import { ListWalkNavigationProps } from './ListWalkNavigation';

export interface ListIndexNavigationProps {
  minIdx?: number;
  maxIdx?: number;
  currentIdx?: number;
  onIndexChange: (idx: number) => void;
  Component: (props: ListWalkNavigationProps) => ReactNode;
}

/**
 * Wrapper that allows using walk navigation with widex logic.
 * Instead of next/previous semantics it supplies a single index as the interface and enforces index upper/lower bounds
 */
export const ListIndexNavigation = memo(function ({
  Component,
  minIdx = 0,
  maxIdx,
  currentIdx,
  onIndexChange,
}: ListIndexNavigationProps): ReactElement {
  const nextAvailable: boolean = useMemo(
    () => currentIdx !== undefined && maxIdx !== undefined && currentIdx + 1 < maxIdx,
    [currentIdx, maxIdx]
  );

  const previousAvailable: boolean = useMemo(
    () => currentIdx !== undefined && currentIdx > minIdx,
    [currentIdx, minIdx]
  );

  function handleNext(): void {
    if (currentIdx !== undefined && nextAvailable) onIndexChange(currentIdx + 1);
  }

  function handlePrevious(): void {
    if (currentIdx !== undefined && previousAvailable) onIndexChange(currentIdx - 1);
  }

  return (
    <Component
      onNextItem={nextAvailable ? handleNext : undefined}
      onPreviousItem={previousAvailable ? handlePrevious : undefined}
    />
  );
});
